import React, { useCallback, useEffect } from "react";
import { useAuthStore } from "@/store/authStore.ts";
import { useFormContext } from "react-hook-form";
import AsyncSelectWithSearch from "@/components/AsyncSelectWithSearch";
import { getBatteryOemsReq, getContractorsReq } from "@/api/userApi";
import { getBatteryCapacityReq, getBatteryListReq } from "@/api/batteryApi";
import FormInput from "@/components/FormInput";
import { Box } from "@mui/material";
import FormFileUpload from "@/components/FormUploadInput";
import { FileRecord } from "@/types";
import FormRadioList from "@/components/FormRadioList";
import { numberInputKeyDownValidation } from "@/utils/numberInputKeyDownValidation.ts";

type Props = {
  participantEnrollment?: FileRecord;
  contract?: FileRecord;
  systemSizingTool?: FileRecord;
};

const SecondProjectForm = ({
  participantEnrollment,
  contract,
  systemSizingTool,
}: Props) => {
  const role = useAuthStore((state) => state.role);
  const { control, getValues, watch, setValue } = useFormContext();

  const batteryModelId = watch("batteryModelId");
  const oemId = watch("oemId");
  const participantEnrollmentFormValue = watch("participantEnrollment");
  const contractFormValue = watch("contract");
  const systemSizingToolFormValue = watch("systemSizingTool");

  const getFileInfoObject = (file: File | FileRecord | undefined) => {
    return file
      ? {
          fileName:
            ("name" in file && file?.name) ||
            ("fileName" in file && file?.fileName) ||
            "",
          size: `${file?.size}`,
        }
      : undefined;
  };

  useEffect(() => {
    if (batteryModelId) {
      const fetchBatteryCapacity = async () => {
        try {
          const response = await getBatteryCapacityReq(batteryModelId);
          setValue("usableCapacity", response?.data?.batteryCapacity || "0");
        } catch (e) {
          console.error(e);
        }
      };
      fetchBatteryCapacity();
    }
  }, [batteryModelId]);

  const getContractors = useCallback(
    (searchValue: string) => getContractorsReq(searchValue),
    [],
  );

  const getBatteryOems = useCallback(
    (searchValue: string) => getBatteryOemsReq(searchValue),
    [],
  );

  const getBatteryList = useCallback(
    (searchValue: string) => getBatteryListReq(oemId, searchValue),
    [oemId],
  );

  return (
    <>
      <AsyncSelectWithSearch
        name={"contractor"}
        idKey={"contractorId"}
        disabled={role === "contractor"}
        control={control}
        label={"Contractor"}
        getItems={getContractors}
        itemsKey={"contractors"}
        placeholder={"Select contractor"}
        controlProps={{ rules: { required: "Required" } }}
      />
      <AsyncSelectWithSearch
        name={"batteryOem"}
        idKey={"oemId"}
        control={control}
        disabled={role === "oem"}
        label={"Battery Manufacturer"}
        getItems={getBatteryOems}
        itemsKey={"oems"}
        placeholder={"Select battery OEM"}
        onValueChange={() => {
          setValue("batteryModel", "");
          setValue("batteryModelId", "");
          setValue("usableCapacity", "0");
        }}
      />
      <AsyncSelectWithSearch
        name={"batteryModel"}
        idKey={"batteryModelId"}
        control={control}
        label={"Battery product"}
        disabled={!getValues("batteryOem")}
        getItems={getBatteryList}
        itemsKey={"batteryModels"}
        placeholder={"Select battery model"}
      />
      <FormInput
        name={"usableCapacity"}
        disabled={true}
        control={control}
        label={"Nameplate Usable Capacity (kWh)"}
        placeholder={"To see nameplate capacity select a battery product"}
      />
      <FormInput
        name={"numberOfBatteries"}
        control={control}
        type={"number"}
        label={"Number of batteries"}
        onKeyDown={numberInputKeyDownValidation}
        placeholder={"To see nameplate capacity select a battery product"}
        controlProps={{
          rules: {
            required: "Required",
          },
        }}
      />
      <Box sx={{ display: "flex", gap: "16px" }}>
        <FormInput
          name={"summerEnrolment"}
          control={control}
          label={"Summer Enrollment %"}
          onKeyDown={numberInputKeyDownValidation}
          adornment={"%"}
          type={"number"}
          placeholder={"000"}
          controlProps={{
            rules: {
              required: "Required",
            },
          }}
        />
        <FormInput
          name={"winterEnrolment"}
          control={control}
          label={"Winter Enrollment %"}
          placeholder={"000"}
          onKeyDown={numberInputKeyDownValidation}
          adornment={"%"}
          type={"number"}
          controlProps={{
            rules: {
              required: "Required",
            },
          }}
        />
      </Box>
      <FormRadioList
        name={`participation`}
        control={control}
        label={"Participation"}
        items={[
          { value: "weekdays", label: "Weekdays Only" },
          { value: "weekdays_weekends", label: "Weekdays & Weekends" },
        ]}
        controlProps={{ rules: { required: "Required" } }}
      />
      <FormFileUpload
        name="participantEnrollment"
        control={control}
        label="Participant Enrollment"
        currentFileInfo={
          getFileInfoObject(participantEnrollmentFormValue) ||
          participantEnrollment
        }
        controlProps={{ rules: { required: "Required" } }}
      />
      <FormFileUpload
        name="systemSizingTool"
        control={control}
        label="System Sizing Tool"
        currentFileInfo={
          getFileInfoObject(systemSizingToolFormValue) || systemSizingTool
        }
        controlProps={{ rules: { required: "Required" } }}
      />
      <FormFileUpload
        name="contract"
        control={control}
        label="Contract"
        currentFileInfo={getFileInfoObject(contractFormValue) || contract}
      />
    </>
  );
};

export default SecondProjectForm;
